// Kalendář: zobrazené datum a režim (den, týden), filtr člena, události v rozsahu s cache podle klíče rozsahu.
// Mutace cache zahodí a obnoví aktuální rozsah, takže zobrazení je po uložení vždy čerstvé.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type {
  CreateEventInput,
  EventItem,
  EventMutationResponse,
  EventWarning,
  OrgMember,
  UpdateEventInput,
} from '@wac-buddy/shared'
import {
  createEvent,
  deleteEvent,
  getEvent,
  listConflicts,
  listEvents,
  listMembers,
  updateEvent,
  type ConflictParams,
} from '@/api/events'
import {
  DEFAULT_TIMEZONE,
  rangeFor,
  shiftKey,
  todayKey,
  parseDayKey,
  type CalendarMode,
} from '@/lib/calendar'
import { useAuthStore } from './auth'

export type CalendarStatus = 'idle' | 'loading' | 'ready' | 'error'

/** Varování z posledního uložení, které se ukáže v kalendáři (formulář se po uložení zavře). */
export interface SavedNotice {
  title: string
  warnings: EventWarning[]
}

function defaultMode(): CalendarMode {
  try {
    return window.matchMedia('(min-width: 900px)').matches ? 'week' : 'day'
  } catch {
    return 'day'
  }
}

export const useCalendarStore = defineStore('calendar', () => {
  const mode = ref<CalendarMode>(defaultMode())
  /** Uživatel režim zvolil sám, výchozí podle šířky se už nepřepíná. */
  const modeChosen = ref(false)
  const dateKeyRef = ref('')
  const memberId = ref('')
  const status = ref<CalendarStatus>('idle')
  const cache = ref<Record<string, EventItem[]>>({})
  const members = ref<OrgMember[]>([])
  const membersLoaded = ref(false)
  const notice = ref<SavedNotice | null>(null)

  let loadSeq = 0

  const timeZone = computed(() => useAuthStore().me?.org?.timezone || DEFAULT_TIMEZONE)

  /** Zobrazený den (klíč YYYY-MM-DD); dokud ho nikdo nenastavil, je to dnešek v zóně organizace. */
  const dateKey = computed(() => dateKeyRef.value || todayKey(timeZone.value))
  const range = computed(() => rangeFor(mode.value, dateKey.value, timeZone.value))
  const cacheKey = computed(() => `${range.value.key}:${memberId.value}`)
  const events = computed<EventItem[]>(() => cache.value[cacheKey.value] ?? [])

  async function load(force = false): Promise<void> {
    const key = cacheKey.value
    if (!force && cache.value[key]) {
      status.value = 'ready'
      return
    }
    const seq = ++loadSeq
    status.value = 'loading'
    try {
      const data = await listEvents({
        from: range.value.from,
        to: range.value.to,
        assigneeId: memberId.value || undefined,
      })
      if (seq !== loadSeq) return
      cache.value[key] = data
      status.value = 'ready'
    } catch {
      if (seq !== loadSeq) return
      status.value = 'error'
    }
  }

  async function loadMembers(): Promise<void> {
    if (membersLoaded.value) return
    try {
      members.value = await listMembers()
      membersLoaded.value = true
    } catch {
      members.value = []
    }
  }

  function setDate(key: string): void {
    if (parseDayKey(key)) dateKeyRef.value = key
  }

  function goToday(): void {
    dateKeyRef.value = todayKey(timeZone.value)
  }

  function shift(direction: -1 | 1): void {
    dateKeyRef.value = shiftKey(mode.value, dateKey.value, direction)
  }

  function setMode(next: CalendarMode, chosen = true): void {
    mode.value = next
    if (chosen) modeChosen.value = true
  }

  function setMember(id: string): void {
    memberId.value = id
  }

  function dismissNotice(): void {
    notice.value = null
  }

  function invalidateCache(): void {
    cache.value = {}
  }

  /** Po zápisu: cache pryč a aktuální rozsah znovu. Chyba obnovy zápis nezneplatní, zobrazení si načte samo. */
  async function refresh(): Promise<void> {
    invalidateCache()
    await load(true)
  }

  function fetchEvent(id: string): Promise<EventItem> {
    return getEvent(id)
  }

  function checkConflicts(params: ConflictParams): Promise<EventWarning[]> {
    return listConflicts(params)
  }

  async function create(input: CreateEventInput): Promise<EventMutationResponse> {
    const res = await createEvent(input)
    notice.value = { title: res.event.title, warnings: res.warnings }
    await refresh()
    return res
  }

  async function update(id: string, input: UpdateEventInput): Promise<EventMutationResponse> {
    const res = await updateEvent(id, input)
    notice.value = { title: res.event.title, warnings: res.warnings }
    await refresh()
    return res
  }

  async function remove(id: string): Promise<void> {
    await deleteEvent(id)
    notice.value = null
    await refresh()
  }

  /** Jiný uživatel nebo jiná firma = jiná data. */
  function invalidate(): void {
    invalidateCache()
    members.value = []
    membersLoaded.value = false
    notice.value = null
    memberId.value = ''
    dateKeyRef.value = ''
    status.value = 'idle'
  }

  return {
    mode,
    modeChosen,
    dateKey,
    memberId,
    status,
    members,
    notice,
    timeZone,
    range,
    events,
    load,
    loadMembers,
    setDate,
    goToday,
    shift,
    setMode,
    setMember,
    dismissNotice,
    fetchEvent,
    checkConflicts,
    create,
    update,
    remove,
    invalidate,
    invalidateCache,
  }
})
